/**
 * Fills progressively loaded volumes after the viewer has opened.
 *
 * One controller per open: it walks the series it was given, primary first,
 * fetching each series' remaining slices through a rolling pool, parsing them
 * on the main thread (there is no worker infrastructure in this frontend) and
 * handing each one to the domain, which places it and reports the z row the
 * engine has to push.
 *
 * Framework-free on purpose — every effect it has on the world arrives through
 * {@link SliceStreamDeps}, so its state machine is testable without a GL
 * context, a DICOM file or a React tree.
 *
 * Failure semantics (see the plan, §7):
 * - a slice that is absent, never completes its transfer, or blows its
 *   per-request deadline is marked UNAVAILABLE and never retried. It is never
 *   interpolated: "never arrived" and "synthesized anatomy" must not be the
 *   same state on screen;
 * - a hard transport failure ends that series' streaming, and every slice it
 *   did not reach is marked unavailable too. The image stays explicitly
 *   incomplete; the session survives;
 * - an abort (patient close, viewer teardown, navigation) marks NOTHING. The
 *   slices were not missing, the user just left.
 */

import {
    InstanceNotFoundError,
    RequestDeadlineError,
    fetchInstanceBlob,
    isAbortError,
    type InstanceAddress,
} from '~/backend/connector-next-api/dataRetrieval';
import { RetryTimeoutError, withRetryOn202 } from '~/backend/retryOn202';
import type { DicomImageSlice } from '~/domain/dicom/dicomImageSlice';
import type { Image3D } from '~/domain/patient/image3d';
import { DomainValidationError } from '~/domain/errors/DomainError';

/** One volume to fill and the instances still to fetch for it. */
export type StreamedSeries = {
    /** The volume, built from its manifest and holding its anchor slice. */
    image: Image3D;
    /** Remaining instances, in the order they are requested (z ascending). */
    addresses: readonly InstanceAddress[];
};

/**
 * Warn about one slice without putting PHI in the browser console.
 *
 * Only a {@link DomainValidationError}'s message is safe to print: the domain
 * writes those from counts, indices and sizes by construction. Anything else
 * here comes from the DICOM parser or the engine, whose messages are not
 * written under that rule and have carried a Series Instance UID, so those are
 * identified by type alone.
 */
function warnAboutSlice(what: string, error: unknown): void {
    const detail =
        error instanceof DomainValidationError
            ? error.message
            : error instanceof Error
              ? error.name
              : 'unknown error';
    console.warn(`${what}: ${detail}`);
}

export type SliceStreamDeps = {
    /** Retrieves one instance's bytes; polls while the backend answers 202. */
    fetchSlice: (
        address: InstanceAddress,
        signal: AbortSignal
    ) => Promise<Blob>;
    /** Parses retrieved bytes into a slice. Parse-and-drop: nothing retains. */
    parseSlice: (bytes: ArrayBuffer) => DicomImageSlice;
    /** One slice landed at `zIndex`; push that slab and repaint. */
    onSliceLoaded: (image: Image3D, zIndex: number) => void;
    /**
     * The slice at `zIndex` will never arrive; show that row as absent and
     * repaint. Optional: written-off rows keep the zero they already hold
     * whether or not anyone listens.
     */
    onSliceUnavailable?: (image: Image3D, zIndex: number) => void;
    /**
     * The image's completeness changed. Called after every event that can move
     * it — a slice arriving, a slice being written off, a series ending — so
     * the UI can indicate progress and hold its gates. Optional: the
     * controller's own behaviour does not depend on anyone listening.
     *
     * The controller does not throttle. The recipient decides how often a
     * counter is worth re-rendering; see `StreamProgressThrottle`.
     */
    onProgress?: (image: Image3D) => void;
    /** A series finished retrieval still missing slices. Called at most once. */
    onSeriesIncomplete: (image: Image3D) => void;
    /** Hands the main thread back between parses. */
    yieldToEventLoop: () => Promise<void>;
    /** Concurrent fetches per series. */
    concurrency?: number;
};

/** Matches `downloadInstancesToFiles`, and for the same reason. */
const DEFAULT_CONCURRENCY = 6;

/** Retrieval outcomes that mean "this slice, permanently, not this series". */
function isSkippableSliceError(error: unknown): boolean {
    return (
        error instanceof InstanceNotFoundError ||
        error instanceof RetryTimeoutError ||
        error instanceof RequestDeadlineError
    );
}

/** The default retrieval: the `.dcm` route with 202-polling. */
export function fetchStreamedSlice(
    address: InstanceAddress,
    signal: AbortSignal
): Promise<Blob> {
    return withRetryOn202(() => fetchInstanceBlob(address, signal), {
        signal,
        timeoutMessage: 'Instance transfer did not complete before timeout',
    });
}

/** Default main-thread yield: a macrotask, so input and paint get a turn. */
export function yieldToEventLoop(): Promise<void> {
    return new Promise<void>((resolve) => setTimeout(resolve, 0));
}

export class SliceStreamController {
    private readonly controller = new AbortController();
    private started = false;

    public constructor(private readonly deps: SliceStreamDeps) {}

    /** Whether {@link abort} has been called. */
    public get aborted(): boolean {
        return this.controller.signal.aborted;
    }

    /**
     * Streams every series in order, and never rejects: a controller that
     * threw would take the viewer session down with it, and the session is
     * exactly what this must not disturb.
     *
     * @param series Volumes to fill, in fetch order (primary series first).
     */
    public async run(series: readonly StreamedSeries[]): Promise<void> {
        if (this.started) {
            throw new Error('SliceStreamController.run called twice');
        }
        this.started = true;

        for (const target of series) {
            if (this.controller.signal.aborted) {
                return;
            }
            try {
                await this.streamSeries(target);
            } catch (error) {
                if (isAbortError(error) || this.controller.signal.aborted) {
                    return;
                }
                // Containment: whatever went wrong with this series, the
                // remaining ones and the session carry on.
                console.warn(
                    'Image slice streaming failed for a series: ' +
                        `${error instanceof Error ? error.message : error}`
                );
                this.markPendingUnavailable(target.image);
                this.reportIfIncomplete(target.image);
            }
        }
    }

    /** Stops every in-flight fetch. Marks nothing unavailable. */
    public abort(): void {
        this.controller.abort();
    }

    /**
     * One series: a rolling pool of fetches in z order, each parsed and
     * inserted as it lands.
     */
    private async streamSeries(target: StreamedSeries): Promise<void> {
        const { image, addresses } = target;
        const concurrency = this.deps.concurrency ?? DEFAULT_CONCURRENCY;

        // Internal controller so one worker's hard failure stops this series
        // without touching the next one; the caller's abort forwards into it.
        const seriesAbort = new AbortController();
        const forwardAbort = (): void => seriesAbort.abort();
        if (this.controller.signal.aborted) {
            return;
        }
        this.controller.signal.addEventListener('abort', forwardAbort, {
            once: true,
        });

        let hardFailure = false;
        let next = 0;

        const worker = async (): Promise<void> => {
            // `next++` claims an index synchronously before the first await,
            // so no instance is fetched twice.
            while (next < addresses.length && !seriesAbort.signal.aborted) {
                const address = addresses[next++];
                let blob: Blob;
                try {
                    blob = await this.deps.fetchSlice(
                        address,
                        seriesAbort.signal
                    );
                } catch (error) {
                    if (isAbortError(error) || this.controller.signal.aborted) {
                        return;
                    }
                    if (isSkippableSliceError(error)) {
                        this.markUnavailable(image, address.sopInstanceUid);
                        continue;
                    }
                    // Transport is broken, not this one instance: stop the
                    // series rather than hammering the rest of the stack.
                    hardFailure = true;
                    seriesAbort.abort();
                    return;
                }

                let zIndex: number;
                try {
                    const slice = this.deps.parseSlice(
                        await blob.arrayBuffer()
                    );
                    zIndex = image.insertSlice(slice);
                } catch (error) {
                    if (isAbortError(error) || this.controller.signal.aborted) {
                        return;
                    }
                    warnAboutSlice(
                        'A retrieved image slice could not be inserted',
                        error
                    );
                    this.markUnavailable(image, address.sopInstanceUid);
                    await this.deps.yieldToEventLoop();
                    continue;
                }

                // Past the insert, the slice IS in the volume. A failure from
                // here on must not write it off: the domain refuses to mark an
                // arrived slice unavailable, so the attempt would only produce
                // a second warning, and the volume would still count the slice
                // it genuinely holds.
                try {
                    this.deps.onSliceLoaded(image, zIndex);
                } catch (error) {
                    if (isAbortError(error) || this.controller.signal.aborted) {
                        return;
                    }
                    warnAboutSlice(
                        'A retrieved image slice could not be displayed',
                        error
                    );
                }
                this.reportProgress(image);

                await this.deps.yieldToEventLoop();
            }
        };

        try {
            await Promise.all(
                Array.from(
                    { length: Math.min(concurrency, addresses.length) },
                    () => worker()
                )
            );
        } finally {
            this.controller.signal.removeEventListener('abort', forwardAbort);
        }

        if (this.controller.signal.aborted) {
            return;
        }
        if (hardFailure) {
            console.warn(
                'Image slice streaming stopped after a transport failure; ' +
                    'the image stays incomplete'
            );
            this.markPendingUnavailable(image);
        }
        this.reportIfIncomplete(image);
    }

    /**
     * Terminal state reached with slices missing: say so, once, per series.
     *
     * Progress is reported either way — reaching the end of a series is the
     * moment "still loading" becomes either "complete" or "incomplete, and it
     * will stay that way", and both flip UI state.
     */
    private reportIfIncomplete(image: Image3D): void {
        this.reportProgress(image);
        if (image.isIncompleteFinal) {
            this.deps.onSeriesIncomplete(image);
        }
    }

    /**
     * Publishes completeness. Never lets a listener's failure reach the stream:
     * a broken indication must not stop slices from arriving.
     */
    private reportProgress(image: Image3D): void {
        try {
            this.deps.onProgress?.(image);
        } catch (error) {
            console.warn(
                'An image streaming progress listener failed: ' +
                    `${error instanceof Error ? error.message : error}`
            );
        }
    }

    /** Every slice still expected will never arrive. */
    private markPendingUnavailable(image: Image3D): void {
        for (const sopUid of image.pendingSliceSopUids) {
            this.markUnavailable(image, sopUid);
        }
    }

    /**
     * The domain rejects marking a slice that already arrived or that the
     * manifest does not list; neither is worth ending the stream over.
     */
    private markUnavailable(image: Image3D, sopInstanceUid: string): void {
        let zIndex: number;
        try {
            zIndex = image.markSliceUnavailable(sopInstanceUid);
        } catch (error) {
            warnAboutSlice('Could not mark an image slice unavailable', error);
            return;
        }
        this.deps.onSliceUnavailable?.(image, zIndex);
        // Writing a slice off moves the counters too — it drops the pending
        // count, and on the last pending slice it flips the image into
        // incomplete-final. The indication must not wait for the end of the
        // series to say so.
        this.reportProgress(image);
    }
}
